import { Controller } from "@hotwired/stimulus"
import { registerServiceWorker, checkForUpdate } from "../pwa/registration"

// Shows the "Update available" pill (layouts/_reload_prompt.html.erb) once a
// new service worker has installed and is waiting behind the one in control.
// Refresh posts SKIP_WAITING to the waiting worker and reloads when it takes
// over; the cross hides the pill for the rest of the browser session.
//
// The element re-renders on every Turbo visit, so connect() also looks for
// a worker that was already waiting when the visit happened.
const DISMISSED_KEY = "winote_reload_dismissed"

export default class extends Controller {
  connect() {
    this.reloading = false
    this.onUpdateFound = () => this.watchInstalling()
    this.onControllerChange = () => { if (this.reloading) window.location.reload() }
    navigator.serviceWorker?.addEventListener("controllerchange", this.onControllerChange)

    registerServiceWorker().then((registration) => {
      if (!registration) return
      this.registration = registration
      registration.addEventListener("updatefound", this.onUpdateFound)
      if (registration.waiting && navigator.serviceWorker.controller) this.show()
      else if (registration.installing) this.watchInstalling()
      checkForUpdate(registration)
    })
  }

  disconnect() {
    navigator.serviceWorker?.removeEventListener("controllerchange", this.onControllerChange)
    this.registration?.removeEventListener("updatefound", this.onUpdateFound)
  }

  refresh() {
    const waiting = this.registration?.waiting
    if (!waiting) { window.location.reload(); return }

    this.reloading = true
    waiting.postMessage({ type: "SKIP_WAITING" })
  }

  dismiss() {
    this.element.hidden = true
    try { sessionStorage.setItem(DISMISSED_KEY, "1") } catch (e) { /* private mode: best effort */ }
  }

  // A worker reaching "installed" while another one controls the page is an
  // update. On the very first install there is no controller, and nothing to
  // announce: the page is already running the latest version.
  watchInstalling() {
    const worker = this.registration?.installing
    if (!worker) return
    worker.addEventListener("statechange", () => {
      if (worker.state === "installed" && navigator.serviceWorker.controller) this.show()
    })
  }

  show() {
    if (this.dismissed()) return
    this.element.hidden = false
  }

  dismissed() {
    try { return sessionStorage.getItem(DISMISSED_KEY) === "1" } catch (e) { return false }
  }
}
